import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import firebase from "gatsby-plugin-firebase";

// Initial state
export const initialState = {
  firstName: "",
  lastName: "",
  username: "",
  pronouns: "",
  email: "",
  tags: [],
  onboarding: {},
  companies: [],
  isLoading: false,
  hasError: false,
  hasFulfilled: false,
  isEngaged: false,
};

// Thunks
export const loadAccount = createAsyncThunk("account/getAccount", async () => {
  try {
    const auth = firebase.auth();
    const db = firebase.firestore();

    const snapshot = await db
      .collection("accounts")
      .doc(auth.currentUser.uid)
      .get();
    if (!snapshot.exists) {
      // This user doesn't have an account
      return undefined;
    } else {
      const data = snapshot.data();
      // If a user reverses an email change for security reasons
      // this block of code will rewrite the database to remain aligned
      if (auth.currentUser.email !== data.email) {
        db.collection("accounts").doc(auth.currentUser.uid).update({
          email: auth.currentUser.email,
        });
        return {
          ...data,
          email: auth.currentUser.email,
        };
      } else {
        return data;
      }
    }
  } catch (error) {
    firebase.analytics().logEvent("load_account", {
      errorCode: error.code,
      errorMsg: error.message,
    });

    return undefined;
  }
});

// Slice
export const accountSlice = createSlice({
  name: "account",
  initialState: initialState,
  reducers: {
    setAccount: (state, action) => action.payload,
    updateAccount: (state, action) => {
      state[action.payload.field] = action.payload.value;
    },
    updateOnboarding: (state, action) => {
      if (state.onboarding !== undefined) {
        state.onboarding[action.payload.field] = action.payload.value;
      } else {
        state.onboarding = { [action.payload.field]: action.payload.value };
      }
    },
  },
  extraReducers: {
    [loadAccount.pending]: (state, action) => {
      state.isLoading = true;
      state.hasError = false;
      state.hasFulfilled = false;
    },
    [loadAccount.fulfilled]: (state, action) => {
      if (typeof action.payload === "object") {
        state.firstName = action.payload.firstName;
        state.lastName = action.payload.lastName;
        state.username = action.payload.username;
        state.pronouns = action.payload.pronouns;
        state.email = action.payload.email;
        state.tags = action.payload.tags;
        state.companies = action.payload.companies || [];
        state.onboarding = action.payload.onboarding;
        state.isEngaged = action.payload.isEngaged || false;
      }
      state.isLoading = false;
      state.hasError = false;
      state.hasFulfilled = true;
    },
    [loadAccount.rejected]: (state, action) => {
      state.isLoading = false;
      state.hasError = true;
      state.hasFulfilled = true;
    },
  },
});

// Selectors
export const selectAccount = (state) => state.account;
export const selectAccountHasFulfilled = (state) => state.account.hasFulfilled;
export const selectIsEmployer = (state) => {
  if (state.account.tags !== undefined) {
    return state.account.tags.includes("employer");
  } else {
    return false;
  }
};
export const selectDomain = (state) => {
  if (state.account.email !== undefined) {
    const splitEmail = state.account.email.split("@");
    return splitEmail[1];
  } else {
    return "";
  }
};
export const selectisEngaged = (state) => state.account.isEngaged;

// Exports
export const { setAccount, updateAccount, updateOnboarding } =
  accountSlice.actions;
export default accountSlice.reducer;
